import React from "react";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Unstable_Grid2";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useFormContext,
} from "react-hook-form";
import { styled, useTheme } from "@mui/material/styles";
import { v4 as uuidv4 } from "uuid";

import { inputLabelStyles } from "./styles";

import TagsEditor from "./TagsEditor";
import ImageEditorPreview from "./ImageEditorPreview";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import CancelIcon from "@mui/icons-material/Cancel";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { ImageObfuscationCandidate } from "constructs/TaggedImage";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { InputLabel, Typography } from "@mui/material";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export type ImageType = "banner" | "post" | "project" | "character";

export interface ImageField {
  uri: string;
  name: string;
  obfuscationStr: string;
  styles: string;
  kind: string;
}

export interface ImagesEditorProps {
  name: string;
  allowUploads?: boolean | undefined;
  defaultImageType?: string | undefined;
}


const ImagesEditor = ({
  allowUploads,
  defaultImageType,
  name,
}: ImagesEditorProps) => {
  const theme = useTheme();
  const methods = useFormContext();
  const { fields, append, prepend, remove, swap, move, insert, update } =
    useFieldArray({ name, control: methods.control });

  const disallowUploads = !(allowUploads ?? false);
  const [imageThumbnails, setImageThumbnails] = React.useState<Array<string>>(
    []
  );

  const [imageObfuscations, setImageObuscations] = React.useState<Array<any>>(
    []
  );

  const handleImageKindChange =
    (index: number) => (event: SelectChangeEvent) => {
      const value = event.target.value as string;
      methods.setValue(`${name}.${index}.kind`, value);
    };

  const handleAddImage = () => {
    const defaultImageField: ImageField = {
      uri: "https://tenkizora.com/images/",
      name: "",
      obfuscationStr: "[]",
      styles: "{}",
      kind: defaultImageType ?? "",
    };

    append(defaultImageField);
    setImageThumbnails([...imageThumbnails, ""]);
    setImageObuscations([...imageObfuscations, []]);
  };

  const handleRemoveImage =
    (index: number) =>
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      remove(index);
      imageThumbnails.splice(index, 1);
    };

  const selectImageUpload =
    (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;

      if (!files?.length) {
        return;
      }

      const objUrl = URL.createObjectURL(files[0]);

      imageThumbnails.splice(index, 1, objUrl);
      setImageThumbnails([...imageThumbnails]);
    };

  const deselectImageUpload =
    (index: number) =>
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const currentField = methods.getValues(`${name}.${index}`);
      delete currentField["image"];

      update(index, {
        ...currentField,
      });

      // Reset image thumbnail to none
      imageThumbnails.splice(index, 1, "");

      // Reset the image obfuscations to zero
      imageObfuscations.splice(index, 1, []);

      // set the image Thumbnails and image obfuscations so that page is updated
      setImageThumbnails([...imageThumbnails]);
      setImageObuscations([...imageObfuscations]);
    };

  const handleImageClick =
    (index: number) => (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
      const { xPercent, yPercent } = getCursorCoord(event);

      let obfuscations = imageObfuscations[
        index
      ] as Array<ImageObfuscationCandidate>;

      obfuscations.push({
        key: uuidv4(),
        element: "circle",
        cx: `${xPercent.toFixed(1)}%`,
        cy: `${yPercent.toFixed(1)}%`,
        r: "4%",
      });

      imageObfuscations.splice(index, 1, obfuscations);

      setImageObuscations([...imageObfuscations]);
      methods.setValue(
        `${name}.${index}.obfuscationStr`,
        JSON.stringify(obfuscations),
        { shouldDirty: true, shouldTouch: true, shouldValidate: true }
      );
    };

  const handleRemoveObfuscation =
    (index: number) => (key: number | string) => (event: any) => {
      event.preventDefault();
      event.stopPropagation();

      const obfuscations: Array<ImageObfuscationCandidate> =
        imageObfuscations[index];
      if (!obfuscations || !Array.isArray(obfuscations)) return;

      let currentObfuscationIndex = obfuscations.findIndex(
        (ob) => ob.key === key
      );

      if (currentObfuscationIndex === -1) {
        return;
      }

      // const currentObf = obfuscations[currentObfuscationIndex];

      obfuscations.splice(currentObfuscationIndex, 1);

      setImageObuscations([...imageObfuscations]);
      methods.setValue(
        `${name}.${index}.obfuscationStr`,
        JSON.stringify(obfuscations),
        { shouldDirty: true, shouldTouch: true, shouldValidate: true }
      );
    };

  const handleResizeObfuscation =
    (index: number) => (key: string | number) => (event: any) => {
      // event.preventDefault();  // { passive: false } is not set so it would be ignored anyway
      const obfuscations: Array<ImageObfuscationCandidate> =
        imageObfuscations[index];
      if (!obfuscations || !Array.isArray(obfuscations)) return;

      let currentObfuscationIndex = obfuscations.findIndex(
        (ob) => ob.key === key
      );

      if (currentObfuscationIndex === -1) {
        return;
      }

      const currentObf = obfuscations[currentObfuscationIndex];

      let newRadius: number;
      if (typeof currentObf.r === "string") {
        newRadius = parseFloat(currentObf.r.toString().slice(0, -1));
      } else {
        newRadius = currentObf.r;
      }

      newRadius += event.deltaY * 0.02;

      newRadius = Math.min(100, newRadius);
      newRadius = Math.max(1, newRadius);

      const newObf = {
        ...currentObf,
        r: `${newRadius.toFixed(1)}%`,
      };

      obfuscations.splice(currentObfuscationIndex, 1, newObf);

      setImageObuscations([...imageObfuscations]);
      methods.setValue(
        `${name}.${index}.obfuscationStr`,
        JSON.stringify(obfuscations)
      );
    };

  const handleAssignObfuscationTag =
    (index: number) =>
    (key: number | string | undefined, tagId: number) =>
    (event: any) => {
      // event.preventDefault();
      event.stopPropagation();

      if (typeof key === "undefined") {
        return;
      }

      const obfuscations: Array<ImageObfuscationCandidate> =
        imageObfuscations[index];

      if (!obfuscations || !Array.isArray(obfuscations)) return;

      let currentObfuscationIndex = obfuscations.findIndex(
        (ob) => ob.key === key
      );

      if (currentObfuscationIndex === -1) {
        return;
      }

      const currentObf = obfuscations[currentObfuscationIndex];

      const currentTags = currentObf.tags ?? [];

      const tagIndex = currentTags.indexOf(tagId);

      if (tagIndex === -1) {
        currentTags.push(tagId);
      } else {
        currentTags.splice(tagIndex, 1);
      }

      const newObf = {
        ...currentObf,
        tags: [...currentTags],
      };

      obfuscations.splice(currentObfuscationIndex, 1, newObf);

      setImageObuscations([...imageObfuscations]);
      methods.setValue(
        `${name}.${index}.obfuscationStr`,
        JSON.stringify(obfuscations)
      );
    };

  const getCursorCoord = <T extends Element>(
    event: React.MouseEvent<T, MouseEvent>
  ) => {
    let rect = event.currentTarget.getBoundingClientRect();
    const xPos = event.clientX - rect.left;
    const yPos = event.clientY - rect.top;

    let xPercent = (xPos / rect.width) * 100;
    let yPercent = (yPos / rect.height) * 100;
    xPercent = xPercent > 100 ? 100 : xPercent;
    xPercent = xPercent < 0 ? 0 : xPercent;

    yPercent = yPercent > 100 ? 100 : yPercent;
    yPercent = yPercent < 0 ? 0 : yPercent;

    return {
      x: xPos,
      y: yPos,
      xPercent,
      yPercent,
    };
  };

  return (
    <FormProvider {...methods}>
      <Stack spacing={2}>
        {fields.map((field, index) => (
          <Stack key={field.id} direction="row" spacing={2} alignItems="center">
            <Grid container sx={{ width: "100%" }}>
              <Grid xs={12} lg={6}>
                <Stack spacing={2}>
                  <VisuallyHiddenInput
                    {...methods.register(`${name}.${index}.uid`, {
                      required: true,
                    })}
                    value={uuidv4()}
                  />

                  <FormControl>
                    <Controller
                      control={methods.control}
                      name={`${name}.${index}.title`}
                      render={() => (
                        <TextField
                          required
                          InputLabelProps={inputLabelStyles(theme)}
                          sx={{
                            width: 536,
                            ".MuiInputBase-root": {
                              backgroundColor: theme.palette.background.paper,
                            },
                          }}
                          label="Image Title"
                          {...methods.register(`${name}.${index}.title`, {
                            required: true,
                          })}
                        />
                      )}
                    />
                  </FormControl>

                  <FormControl>
                    <Controller
                      control={methods.control}
                      name={`${name}.${index}.name`}
                      render={() => (
                        <TextField
                          required
                          InputLabelProps={inputLabelStyles(theme)}
                          sx={{
                            width: 536,
                            ".MuiInputBase-root": {
                              backgroundColor: theme.palette.background.paper,
                            },
                          }}
                          label="Image Name"
                          {...methods.register(`${name}.${index}.name`, {
                            required: true,
                          })}
                        />
                      )}
                    />
                  </FormControl>

                  <FormControl>
                    <Controller
                      control={methods.control}
                      name={`${name}.${index}.uri`}
                      render={() => (
                        <TextField
                          required
                          InputLabelProps={inputLabelStyles(theme)}
                          sx={{
                            width: 536,
                            ".MuiInputBase-root": {
                              backgroundColor: theme.palette.background.paper,
                            },
                          }}
                          label="Image Uri"
                          {...methods.register(`${name}.${index}.uri`, {
                            required: true,
                          })}
                        />
                      )}
                    />
                  </FormControl>

                  <FormControl>
                    <Controller
                      control={methods.control}
                      name={`${name}.${index}.description`}
                      render={() => (
                        <TextField
                          required
                          minRows={4}
                          InputLabelProps={inputLabelStyles(theme)}
                          sx={{
                            width: 536,
                            ".MuiInputBase-root": {
                              backgroundColor: theme.palette.background.paper,
                            },
                          }}
                          label="Description"
                          {...methods.register(`${name}.${index}.description`, {
                            required: true,
                          })}
                        />
                      )}
                    />
                  </FormControl>

                  <FormControl>
                    <InputLabel
                      required
                      id="image-type-label"
                      {...inputLabelStyles(theme)}
                    >
                      Image Type
                    </InputLabel>
                    <Controller
                      control={methods.control}
                      name={`${name}.${index}.kind`}
                      render={() => (
                        <Select
                          required
                          labelId="image-type-label"
                          inputProps={{
                            sx: {
                              backgroundColor: theme.palette.background.paper,
                            },
                          }}
                          sx={{
                            width: 536,
                            ".MuiInputBase-root": {
                              backgroundColor: theme.palette.background.paper,
                            },
                          }}
                          {...methods.register(`${name}.${index}.kind`, {
                            required: true,
                          })}
                          onChange={handleImageKindChange(index)}
                          value={methods.getValues(`${name}.${index}.kind`)}
                        >
                          <MenuItem value="banner">Banner</MenuItem>
                          <MenuItem value="project">Project</MenuItem>
                          <MenuItem value="post">Post</MenuItem>
                        </Select>
                      )}
                    />
                  </FormControl>

                  <Accordion
                    disableGutters
                    sx={{
                      ".MuiAccordion-root": { boxShadow: "none !important" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ bgcolor: theme.palette.background.default }}
                    >
                      <Typography color="primary" fontWeight="bold">
                        Advanced Settings
                      </Typography>
                    </AccordionSummary>

                    <AccordionDetails
                      sx={{
                        bgcolor: theme.palette.background.default,
                        border: `1px solid ${theme.palette.secondary.main}`,
                      }}
                    >
                      <Stack spacing={2}>
                        <Stack direction="row" spacing={2}>
                          <FormControl>
                            <Controller
                              control={methods.control}
                              name={`${name}.${index}.unifiedId`}
                              render={() => (
                                <TextField
                                  minRows={4}
                                  InputLabelProps={inputLabelStyles(theme)}
                                  sx={{
                                    width: 260,
                                    ".MuiInputBase-root": {
                                      backgroundColor:
                                        theme.palette.background.paper,
                                    },
                                  }}
                                  label="Unified Id"
                                  {...methods.register(
                                    `${name}.${index}.unifiedId`
                                  )}
                                />
                              )}
                            />
                          </FormControl>
                                    
                          <FormControl>
                            <Controller
                              control={methods.control}
                              name={`${name}.${index}.variant`}
                              render={() => (
                                <TextField

                                  minRows={4}
                                  InputLabelProps={inputLabelStyles(theme)}
                                  sx={{
                                    width: 260,
                                    ".MuiInputBase-root": {
                                      backgroundColor:
                                        theme.palette.background.paper,
                                    },
                                  }}
                                  label="Variant"
                                  {...methods.register(
                                    `${name}.${index}.variant`
                                  )}
                                />
                              )}
                            />
                          </FormControl>
                        </Stack>

                        <FormControl>
                          <Controller
                            control={methods.control}
                            name={`${name}.${index}.styles`}
                            render={() => (
                              <TextField
                                minRows={4}
                                InputLabelProps={inputLabelStyles(theme)}
                                sx={{
                                  width: 536,
                                  ".MuiInputBase-root": {
                                    backgroundColor:
                                      theme.palette.background.paper,
                                  },
                                }}
                                label="Styles Object"
                                {...methods.register(`${name}.${index}.styles`)}
                              />
                            )}
                          />
                        </FormControl>

                        <FormControl>
                          <Controller
                            control={methods.control}
                            name={`${name}.${index}.obfuscationStr`}
                            render={() => (
                              <TextField
                                label="Obfuscation JSON"
                                minRows={4}
                                InputLabelProps={inputLabelStyles(theme)}
                                sx={{
                                  width: 536,
                                  ".MuiInputBase-root": {
                                    backgroundColor:
                                      theme.palette.background.paper,
                                  },
                                }}
                                // label="Obfuscation JSON"
                                {...methods.register(
                                  `${name}.${index}.obfuscationStr`
                                )}
                              />
                            )}
                          />
                        </FormControl>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                </Stack>
              </Grid>

              <Grid xs={12} lg={6}>
                <Stack spacing={2}>
                  <ImageEditorPreview
                    imgSrc={imageThumbnails[index]}
                    obfuscations={imageObfuscations[index]}
                    ImageProps={{ width: "264px", height: "auto" }}
                    tags={methods.getValues(`${name}.${index}.tags`) ?? []}
                    onClick={handleImageClick(index)}
                    resizeObfuscation={handleResizeObfuscation(index)}
                    removeObfuscation={handleRemoveObfuscation(index)}
                    assignObfuscationTag={handleAssignObfuscationTag(index)}
                  />
                  <Tooltip
                    title={
                      disallowUploads
                        ? "Image uploading is disabled from this interface. Please use the Image Editor or upload manually. Note that you should still set the image uri to ensure that the correct image is used later"
                        : "Upload an image"
                    }
                  >
                    <span>
                      <FormControl>
                        <Controller
                          control={methods.control}
                          name={`${name}.${index}.image`}
                          render={() => (
                            <Button
                              key={field.id}
                              component="label"
                              variant={
                                disallowUploads ? "contained" : "outlined"
                              }
                              sx={{
                                color: disallowUploads
                                  ? "red !important"
                                  : "inherit",
                              }}
                              startIcon={
                                imageThumbnails[index] ? (
                                  <CloudSyncIcon />
                                ) : (
                                  <CloudUploadIcon />
                                )
                              }
                              disabled={disallowUploads}
                            >
                              {imageThumbnails[index]
                                ? "Change Image"
                                : "Upload Image"}
                              <VisuallyHiddenInput
                                disabled={disallowUploads}
                                type="file"
                                multiple={false}
                                {...methods.register(`${name}.${index}.image`)}
                                onChange={selectImageUpload(index)}
                              />
                            </Button>
                          )}
                        />
                      </FormControl>
                    </span>
                  </Tooltip>
                  <span>
                    <Tooltip title="Remove the currently uploaded image">
                      <Button onClick={deselectImageUpload(index)}>
                        Remove Image
                      </Button>
                    </Tooltip>
                  </span>
                  <span>
                    <Tooltip title="Delete this image and all related data">
                      <Button
                        startIcon={<CancelIcon />}
                        onClick={handleRemoveImage(index)}
                      >
                        Delete
                      </Button>
                    </Tooltip>
                  </span>
                </Stack>
              </Grid>
              <Grid xs={12}>
                <TagsEditor name={`${name}.${index}.tags`} />
              </Grid>
            </Grid>
          </Stack>
        ))}
        <Stack direction="row">
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddImage}
          >
            Add Image
          </Button>
        </Stack>
      </Stack>
    </FormProvider>
  );
};

export default ImagesEditor;
